'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { adminService } from '@/lib/admin';
import { NotificationBell } from '@/components/NotificationBell';
import { SearchDialog } from '@/components/SearchDialog';
import { SearchDropdown } from '@/components/SearchDropdown';
import { Search, User, Film, Settings, LogOut, LogIn, Shield, ArrowLeft } from 'lucide-react';

interface HeaderProps {
  showSearch?: boolean;
  transparent?: boolean;
  page?: 'landing' | 'dashboard' | 'detail';
}

/**
 * Sleek minimal app header (the /design-preview look) — used app-wide.
 * Full logo (seasonal-aware), Home/My List links, a search icon that opens the
 * search dialog, notifications, and a profile dropdown. Preserves the PWA banner
 * offset + notch safe-area.
 */
export function SleekHeader({ showSearch = true }: HeaderProps) {
  const { user, userProfile, logout } = useAuth();
  const { activeTheme, loading: themeLoading } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  // Show a back button on mobile for inner pages (not the home page).
  const showBack = pathname !== '/' && pathname !== '/design-preview';
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);

  const logoSrc = activeTheme?.logo?.url || '/image/epify.png';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let active = true;
    if (user) {
      adminService.isUserAdmin(user.uid).then((v) => { if (active) setIsAdmin(v); }).catch(() => {});
    } else {
      setIsAdmin(false);
    }
    return () => { active = false; };
  }, [user]);

  const initial = (userProfile?.displayName || userProfile?.username || user?.email || 'U')
    .charAt(0)
    .toUpperCase();
  const photo = userProfile?.photoURL || user?.photoURL;

  // paddingTop = base header padding + notch safe-area, so the safe-area inset
  // doesn't clobber the vertical padding (env() resolves to 0 on desktop).
  const headerStyle = {
    top: 'var(--pwa-banner-height, 0px)',
    paddingTop: `calc(${scrolled ? '1rem' : '1.5rem'} + env(safe-area-inset-top, 0px))`,
  } as React.CSSProperties;

  const menuLinks = [
    { icon: Film, label: 'My Watchlist', href: '/dashboard' },
    { icon: User, label: 'Profile', href: '/profile' },
    { icon: Settings, label: 'Settings', href: '/settings' },
  ];

  return (
    <>
      <header
        style={headerStyle}
        className={`fixed left-0 right-0 z-50 flex items-center justify-between px-5 md:px-10 transition-all duration-300 ${
          scrolled ? 'pb-4 bg-black/80 backdrop-blur-xl' : 'pb-6 bg-gradient-to-b from-black/70 to-transparent'
        }`}
      >
        {/* Left: back button (mobile, inner pages) + logo (links home) */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {showBack && (
            <button
              onClick={() => router.back()}
              aria-label="Go back"
              className="md:hidden flex items-center justify-center h-10 w-10 -ml-2 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <Link href="/" className="flex items-center transition-opacity hover:opacity-80">
            {!themeLoading && (
              <Image src={logoSrc} alt="Epify — Home" width={130} height={40} className="h-8 w-auto" priority />
            )}
          </Link>
        </div>

        {/* Center: search bar (desktop) */}
        {showSearch && (
          <div className="hidden md:block flex-1 max-w-xl mx-6">
            <SearchDropdown placeholder="Search movies, TV shows, people, and users..." />
          </div>
        )}

        {/* Right: actions */}
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          {/* Mobile search icon (opens dialog) */}
          {showSearch && (
            <button
              aria-label="Search"
              onClick={() => setShowSearchDialog(true)}
              className="md:hidden flex items-center justify-center h-10 w-10 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              <Search className="h-5 w-5" />
            </button>
          )}

          {user ? (
            <>
              {/* My List */}
              <Link
                href="/dashboard"
                aria-label="My List"
                className="flex items-center justify-center h-10 w-10 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Film className="h-5 w-5" />
              </Link>
              <NotificationBell />
              <div className="relative">
                <button
                  aria-label="Open account menu"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex items-center justify-center h-10 w-10 rounded-full ring-2 ring-white/10 hover:ring-red-500/50 transition-all overflow-hidden"
                >
                  <span className="h-9 w-9 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center overflow-hidden">
                    {photo ? (
                      <Image src={photo} alt="Profile" width={36} height={36} className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold text-white">{initial}</span>
                    )}
                  </span>
                </button>

                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div className="absolute right-0 mt-3 w-64 z-50 rounded-2xl bg-zinc-950/95 backdrop-blur-xl ring-1 ring-white/10 shadow-2xl overflow-hidden">
                      {/* Profile header */}
                      <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
                        <span className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {photo ? (
                            <Image src={photo} alt="Profile" width={40} height={40} className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-sm font-bold text-white">{initial}</span>
                          )}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white truncate leading-tight">
                            {userProfile?.displayName || userProfile?.username || user.email?.split('@')[0]}
                          </p>
                          {userProfile?.username && (
                            <p className="text-xs text-gray-500 truncate">@{userProfile.username}</p>
                          )}
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="py-1.5">
                        {menuLinks.map(({ icon: Icon, label, href }) => (
                          <button
                            key={href}
                            onClick={() => { setMenuOpen(false); router.push(href); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                          >
                            <Icon className="h-[18px] w-[18px] text-gray-500" /> {label}
                          </button>
                        ))}
                        {isAdmin && (
                          <button
                            onClick={() => { setMenuOpen(false); window.open('/admin', '_blank'); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                          >
                            <Shield className="h-[18px] w-[18px] text-gray-500" /> Admin Panel
                          </button>
                        )}
                      </div>

                      {/* Log out */}
                      <div className="border-t border-white/10 py-1.5">
                        <button
                          onClick={async () => { setMenuOpen(false); await logout(); router.push('/'); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-red-600/15 transition-colors"
                        >
                          <LogOut className="h-[18px] w-[18px] text-red-400" /> Log out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <Link
              href="/auth/signin"
              className="flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-gray-200 transition-colors"
            >
              <LogIn className="h-4 w-4" /> Sign In
            </Link>
          )}
        </div>
      </header>

      <SearchDialog open={showSearchDialog} onClose={() => setShowSearchDialog(false)} />
    </>
  );
}
